<template>
    <div class="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">

        <!-- Loading State -->
        <div v-if="loading || isAnalyzing" class="p-6">
            <div class="flex flex-col items-center justify-center py-8">
                <ticto-loading v-if="!isAnalyzing" />
                <div v-if="isAnalyzing" class="w-full max-w-md">
                    <div class="mb-4 text-center">
                        <span class="text-gray-600 dark:text-gray-400 text-sm font-medium">{{ analysisMessage }}</span>
                    </div>
                    <div class="relative">
                        <div class="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                                class="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500 ease-out"
                                :style="`width: ${analysisProgress}%`"
                            >
                                <div class="absolute inset-0 opacity-25">
                                    <div class="h-full w-full bg-stripes animate-slide"></div>
                                </div>
                            </div>
                        </div>
                        <div class="mt-2 flex justify-between items-center">
                            <span class="text-xs text-gray-500 dark:text-gray-400">Processando...</span>
                            <span class="text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ analysisProgress }}%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="p-6">
            <div class="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <div class="flex items-center">
                    <ExclamationTriangleIcon class="h-5 w-5 text-red-600 mr-2" />
                    <span class="text-red-800 dark:text-red-300">Erro ao carregar análise AI</span>
                </div>
            </div>
        </div>

        <!-- AI Analysis Content -->
        <div v-else-if="aiAnalysis">
            <!-- Header com Score e Confidence -->
            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-4">
                        <!-- Score Visual -->
                        <div class="relative">
                            <div class="w-20 h-20">
                                <svg class="transform -rotate-90 w-20 h-20">
                                    <circle
                                        cx="40"
                                        cy="40"
                                        r="36"
                                        stroke="currentColor"
                                        stroke-width="8"
                                        fill="none"
                                        class="text-gray-200 dark:text-gray-700"
                                    />
                                    <circle
                                        cx="40"
                                        cy="40"
                                        r="36"
                                        :stroke="getScoreColor()"
                                        stroke-width="8"
                                        fill="none"
                                        :stroke-dasharray="`${(aiAnalysis.score / 100) * 226} 226`"
                                        class="transition-all duration-500"
                                    />
                                </svg>
                                <div class="absolute inset-0 flex items-center justify-center">
                                    <span class="text-2xl font-bold" :class="getTextRiskColor()">
                                        {{ aiAnalysis.score }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Títulos e Badges -->
                        <div>
                            <div class="flex items-center gap-2 mb-1">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    Análise AI
                                </h3>
                                <span
                                    class="px-2 py-1 text-xs font-medium rounded-full"
                                    :class="getRiskBadgeClass()"
                                >
                                    Risco {{ getRiskLevelText() }}
                                </span>
                            </div>
                            <div class="flex items-center gap-3 text-sm">
                                <div class="flex items-center gap-1">
                                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                    <span class="text-gray-600 dark:text-gray-400">
                                        Confiança: <strong>{{ getConfidencePercentage() }}%</strong>
                                    </span>
                                </div>
                                <div v-if="aiAnalysis.llm_used" class="flex items-center gap-1">
                                    <span class="text-gray-400">•</span>
                                    <span class="text-xs text-indigo-600 dark:text-indigo-400">
                                        {{ aiAnalysis.llm_model }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Recomendação -->
                    <div class="text-right">
                        <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Recomendação</div>
                        <div class="flex items-center justify-end gap-2">
                            <component
                                :is="getSuggestionIcon()"
                                class="h-5 w-5"
                                :class="getSuggestionIconColor()"
                            />
                            <span class="text-lg font-bold" :class="getSuggestionTextColor()">
                                {{ getSuggestion() }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Risk Factors Principais -->
            <div v-if="aiAnalysis.primary_risk_factors && aiAnalysis.primary_risk_factors.length > 0" class="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Fatores de Risco Principais
                </h4>
                <div class="space-y-2">
                    <div
                        v-for="(factor, index) in aiAnalysis.primary_risk_factors.slice(0, 3)"
                        :key="index"
                        class="flex items-start gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg"
                    >
                        <ExclamationTriangleIcon class="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span class="text-sm text-red-800 dark:text-red-300">{{ factor }}</span>
                    </div>
                </div>
            </div>

            <!-- Métricas Numéricas -->
            <div v-if="getKeyMetrics().length > 0" class="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Métricas Chave
                </h4>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div
                        v-for="metric in getKeyMetrics()"
                        :key="metric.key"
                        class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3"
                    >
                        <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {{ metric.label }}
                        </div>
                        <div class="flex items-baseline gap-1">
                            <span class="text-lg font-bold" :class="metric.colorClass">
                                {{ metric.value }}
                            </span>
                            <span v-if="metric.suffix" class="text-xs text-gray-500">
                                {{ metric.suffix }}
                            </span>
                        </div>
                        <div v-if="metric.comparison" class="text-xs" :class="metric.comparisonClass">
                            {{ metric.comparison }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Score Breakdown -->
            <div v-if="getTopScoreImpacts().length > 0" class="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Impacto no Score
                </h4>
                <div class="space-y-2">
                    <div
                        v-for="impact in getTopScoreImpacts()"
                        :key="impact.rule"
                        class="flex items-center gap-3"
                    >
                        <div class="flex-1">
                            <div class="flex items-center justify-between mb-1">
                                <span class="text-xs text-gray-600 dark:text-gray-400">
                                    {{ humanizeRule(impact.rule) }}
                                </span>
                                <span class="text-xs font-medium" :class="impact.weight > 0 ? 'text-green-600' : 'text-red-600'">
                                    {{ impact.weight > 0 ? '+' : '' }}{{ impact.weight }}
                                </span>
                            </div>
                            <div class="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                    class="h-full rounded-full transition-all duration-300"
                                    :class="impact.weight > 0 ? 'bg-green-500' : 'bg-red-500'"
                                    :style="{ width: Math.abs(impact.weight) + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Padrões Detectados -->
            <div v-if="getUniquePatterns().length > 0" class="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Padrões Detectados
                </h4>
                <div class="flex flex-wrap gap-2">
                    <span
                        v-for="pattern in getUniquePatterns()"
                        :key="pattern.type"
                        class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                        :class="getPatternBadgeClass(pattern.severity)"
                        :title="pattern.description"
                    >
                        {{ humanizePattern(pattern.type) }}
                        <span v-if="pattern.confidence" class="ml-1 opacity-75">
                            ({{ Math.round(pattern.confidence * 100) }}%)
                        </span>
                    </span>
                </div>
            </div>

            <!-- Recomendações Acionáveis -->
            <div v-if="getFilteredRecommendations().length > 0" class="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Ações Recomendadas
                </h4>
                <div class="space-y-2">
                    <div
                        v-for="(rec, index) in getFilteredRecommendations()"
                        :key="index"
                        class="flex items-start gap-2"
                    >
                        <svg class="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                        </svg>
                        <span class="text-sm text-gray-700 dark:text-gray-300">{{ rec }}</span>
                    </div>
                </div>
            </div>

            <!-- Footer com Meta Info -->
            <div class="px-4 py-3 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <span>
                            Analisado {{ getAnalysisTimeAgo() }}
                        </span>
                        <button
                            @click="refreshAnalysis"
                            :disabled="refreshing"
                            class="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
                        >
                            {{ refreshing ? 'Atualizando...' : 'Recarregar' }}
                        </button>
                    </div>
                    <div class="flex items-center gap-2">
                        <button
                            @click="showRawData = true"
                            class="text-xs text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                            Ver Dados Completos
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Fallback when no AI analysis exists -->
        <div v-else class="p-6">
            <div class="text-center py-4 text-gray-500">
                <ExclamationTriangleIcon class="h-8 w-8 mx-auto mb-2" />
                <p class="text-sm">Análise AI não disponível</p>
                <div class="mt-4 space-y-2">
                    <button
                        @click="generateAnalysis"
                        :disabled="isAnalyzing"
                        class="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Gerar Análise AI
                    </button>
                    <p class="text-xs text-gray-400">
                        Clique para gerar uma análise inteligente deste saque
                    </p>
                </div>
            </div>
        </div>

        <!-- Modal de Dados Raw (mantido mas simplificado) -->
        <div v-if="showRawData" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4 text-center">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showRawData = false"></div>

                <div class="relative bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-4xl sm:w-full">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
                                Dados Completos da Análise
                            </h3>
                            <button @click="showRawData = false" class="text-gray-400 hover:text-gray-500">
                                <XMarkIcon class="h-6 w-6" />
                            </button>
                        </div>

                        <div class="max-h-96 overflow-y-auto">
                            <pre class="text-xs bg-gray-100 dark:bg-gray-900 p-4 rounded overflow-x-auto">{{ JSON.stringify(aiAnalysis, null, 2) }}</pre>
                        </div>
                    </div>

                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            @click="showRawData = false"
                            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    ClockIcon,
    XMarkIcon
} from '@heroicons/vue/24/outline';
import TictoLoading from '../../layouts/TictoLoading.vue';
import axios from 'axios';

export default {
    name: 'SmartAnalysis',
    components: {
        CheckCircleIcon,
        ExclamationTriangleIcon,
        XCircleIcon,
        ClockIcon,
        XMarkIcon,
        TictoLoading
    },
    props: {
        review: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            loading: false,
            refreshing: false,
            error: null,
            aiAnalysis: null,
            showRawData: false,
            analysisProgress: 0,
            analysisMessage: '',
            isAnalyzing: false
        };
    },

    mounted() {
        this.loadAIAnalysis();
    },

    watch: {
        'review.cashout.id': {
            handler(newId, oldId) {
                if (newId && newId !== oldId) {
                    this.loadAIAnalysis();
                }
            },
            immediate: false
        }
    },

    methods: {
        async forceReload() {
            return await this.loadAIAnalysis();
        },

        async loadAIAnalysis() {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(`/api/user/${this.review.cashout.id}/ai-analysis`);
                this.aiAnalysis = response.data.analysis;
            } catch (error) {
                console.error('Failed to load AI analysis:', error);
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },

        async generateAnalysis() {
            this.isAnalyzing = true;
            this.analysisProgress = 5;
            this.analysisMessage = 'Iniciando geração da análise AI...';

            try {
                const response = await axios.post(`/api/user/${this.review.cashout.id}/ai-analysis/regenerate`);
                this.aiAnalysis = response.data.analysis;
            } catch (error) {
                console.error('Failed to generate AI analysis:', error);
                this.error = error.response?.data?.message || 'Erro ao gerar análise AI';
            } finally {
                this.isAnalyzing = false;
                this.analysisProgress = 0;
            }
        },

        async refreshAnalysis() {
            this.refreshing = true;

            try {
                const response = await axios.post(`/api/user/${this.review.cashout.id}/ai-analysis/regenerate`);
                this.aiAnalysis = response.data.analysis;
            } catch (error) {
                console.error('Failed to refresh AI analysis:', error);
            } finally {
                this.refreshing = false;
            }
        },

        getScoreColor() {
            if (!this.aiAnalysis) return '#9CA3AF';
            const score = this.aiAnalysis.score;

            if (score >= 80) return '#10B981';
            if (score >= 60) return '#F59E0B';
            if (score >= 30) return '#FB923C';
            return '#EF4444';
        },

        getTextRiskColor() {
            if (!this.aiAnalysis) return 'text-gray-500';
            const score = this.aiAnalysis.score;

            if (score >= 80) return 'text-green-600 dark:text-green-400';
            if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
            if (score >= 30) return 'text-orange-600 dark:text-orange-400';
            return 'text-red-600 dark:text-red-400';
        },

        getRiskBadgeClass() {
            if (!this.aiAnalysis) return 'bg-gray-100 text-gray-800';

            const riskLevel = this.aiAnalysis.risk_level;

            if (riskLevel === 'low') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
            if (riskLevel === 'medium') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
            if (riskLevel === 'high') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
            return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
        },

        getRiskLevelText() {
            if (!this.aiAnalysis) return 'Desconhecido';

            const riskLevel = this.aiAnalysis.risk_level;
            if (riskLevel === 'low') return 'Baixo';
            if (riskLevel === 'medium') return 'Médio';
            if (riskLevel === 'high') return 'Alto';
            return riskLevel;
        },

        getConfidencePercentage() {
            if (!this.aiAnalysis) return 0;
            return this.aiAnalysis.confidence_percentage || Math.round((this.aiAnalysis.confidence || 0) * 100);
        },

        getSuggestion() {
            if (!this.aiAnalysis) return 'ANALISAR';

            if (this.aiAnalysis.recommends_approval) return 'APROVAR';
            if (this.aiAnalysis.recommends_rejection) return 'REJEITAR';
            return 'REVISAR';
        },

        getSuggestionIcon() {
            if (!this.aiAnalysis) return 'ClockIcon';

            if (this.aiAnalysis.recommends_approval) return 'CheckCircleIcon';
            if (this.aiAnalysis.recommends_rejection) return 'XCircleIcon';
            return 'ExclamationTriangleIcon';
        },

        getSuggestionIconColor() {
            if (!this.aiAnalysis) return 'text-gray-600';

            if (this.aiAnalysis.recommends_approval) return 'text-green-600';
            if (this.aiAnalysis.recommends_rejection) return 'text-red-600';
            return 'text-yellow-600';
        },

        getSuggestionTextColor() {
            if (!this.aiAnalysis) return 'text-gray-700 dark:text-gray-400';

            if (this.aiAnalysis.recommends_approval) return 'text-green-700 dark:text-green-400';
            if (this.aiAnalysis.recommends_rejection) return 'text-red-700 dark:text-red-400';
            return 'text-yellow-700 dark:text-yellow-400';
        },

        getKeyMetrics() {
            if (!this.aiAnalysis || !this.aiAnalysis.request_data) return [];

            const data = this.aiAnalysis.request_data;
            const metrics = [];

            // Taxa de Chargeback
            if (data.chargeback_rate !== undefined) {
                metrics.push({
                    key: 'chargeback',
                    label: 'Chargeback',
                    value: data.chargeback_rate.toFixed(2),
                    suffix: '%',
                    colorClass: data.chargeback_rate > 1 ? 'text-red-600' : 'text-green-600',
                    comparison: data.chargeback_rate > 1 ? 'Acima do padrão' : 'Dentro do padrão',
                    comparisonClass: data.chargeback_rate > 1 ? 'text-red-600' : 'text-green-600'
                });
            }

            // Saques Aprovados
            if (data.approved_cashouts !== undefined && data.total_cashouts !== undefined) {
                metrics.push({
                    key: 'cashouts',
                    label: 'Saques',
                    value: `${data.approved_cashouts}/${data.total_cashouts}`,
                    colorClass: 'text-gray-900 dark:text-gray-100',
                    comparison: 'Aprovados',
                    comparisonClass: 'text-green-600'
                });
            }

            // Diversidade de BINs
            if (data.bin_diversity_rate !== undefined) {
                metrics.push({
                    key: 'bin_diversity',
                    label: 'Diversidade BIN',
                    value: Math.round(data.bin_diversity_rate),
                    suffix: '%',
                    colorClass: data.bin_diversity_rate > 80 ? 'text-green-600' : 'text-yellow-600',
                    comparison: data.bin_diversity_rate > 80 ? 'Alta diversidade' : 'Diversidade média',
                    comparisonClass: data.bin_diversity_rate > 80 ? 'text-green-600' : 'text-yellow-600'
                });
            }

            // Taxa de Declínio de Cartão
            const declineRate = data.score_breakdown?.find(r => r.rule === 'credit_card_decline_rate')?.value;
            if (declineRate !== undefined) {
                metrics.push({
                    key: 'decline_rate',
                    label: 'Taxa Declínio',
                    value: declineRate.toFixed(1),
                    suffix: '%',
                    colorClass: declineRate > 5 ? 'text-red-600' : 'text-green-600',
                    comparison: declineRate > 5 ? 'Alta taxa' : 'Taxa normal',
                    comparisonClass: declineRate > 5 ? 'text-red-600' : 'text-green-600'
                });
            }

            return metrics;
        },

        getTopScoreImpacts() {
            if (!this.aiAnalysis || !this.aiAnalysis.request_data?.score_breakdown) return [];

            const impacts = this.aiAnalysis.request_data.score_breakdown
                .filter(item => item.weight !== 0)
                .sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight))
                .slice(0, 5);

            return impacts;
        },

        getUniquePatterns() {
            if (!this.aiAnalysis || !this.aiAnalysis.request_data?.patterns_detected) return [];

            // Remove duplicatas baseadas no tipo
            const uniquePatterns = [];
            const seenTypes = new Set();

            for (const pattern of this.aiAnalysis.request_data.patterns_detected) {
                if (!seenTypes.has(pattern.type)) {
                    seenTypes.add(pattern.type);
                    uniquePatterns.push(pattern);
                }
            }

            return uniquePatterns;
        },

        getPatternBadgeClass(severity) {
            switch (severity) {
                case 'critical':
                    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
                case 'warning':
                    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
                case 'info':
                    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
                default:
                    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
            }
        },

        getFilteredRecommendations() {
            if (!this.aiAnalysis || !this.aiAnalysis.recommendations) return [];

            // Filtra recomendações genéricas ou duplicadas
            const filtered = this.aiAnalysis.recommendations
                .filter(rec => {
                    // Remove recomendações genéricas do sistema
                    if (rec.includes('High score with no critical issues')) return false;
                    if (rec.length < 10) return false;
                    return true;
                })
                .slice(0, 3);

            return filtered;
        },

        getAnalysisTimeAgo() {
            if (!this.aiAnalysis?.analyzed_at) return 'há algum tempo';

            const analyzedAt = new Date(this.aiAnalysis.analyzed_at);
            const now = new Date();
            const diffMinutes = Math.floor((now - analyzedAt) / 1000 / 60);

            if (diffMinutes < 1) return 'agora mesmo';
            if (diffMinutes < 60) return `há ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;

            const diffHours = Math.floor(diffMinutes / 60);
            if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;

            const diffDays = Math.floor(diffHours / 24);
            return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
        },

        humanizeRule(rule) {
            const rules = {
                'chargeback_rate': 'Taxa de Chargeback',
                'credit_card_decline_rate': 'Taxa de Declínio',
                'credit_card_transaction_rate': 'Taxa de Transação com Cartão',
                'bin_diversity_rate': 'Diversidade de BINs',
                'affiliate_bin_diversity': 'Diversidade BIN Afiliados',
                'unique_cards_by_visitor_id': 'Cartões Únicos por Visitante',
                'recent_transactions_diversity': 'Diversidade de Transações',
                'email_bounces': 'Emails com Bounce',
                'is_verified': 'Conta Verificada',
                'has_account_manager': 'Gerente de Conta',
                'configured_integrations': 'Integrações Configuradas',
                'weekly_withdrawals': 'Saques Semanais',
                'successful_withdrawals': 'Saques Bem-Sucedidos',
                'has_exorbitant_ebook_sales': 'Vendas Exorbitantes de E-book',
                'seller_has_sold_early': 'Vendas Precoces',
                'first_sale_after_affiliation': 'Primeira Venda após Afiliação',
                'related_low_score_accounts': 'Contas Relacionadas com Score Baixo'
            };

            return rules[rule] || rule.replace(/_/g, ' ');
        },

        humanizePattern(type) {
            const patterns = {
                'quick_cashout': 'Saque Rápido',
                'high_velocity': 'Alta Velocidade',
                'suspicious_timing': 'Horário Suspeito',
                'low_diversity': 'Baixa Diversidade',
                'high_risk_pattern': 'Padrão de Alto Risco'
            };

            return patterns[type] || type.replace(/_/g, ' ');
        }
    }
};
</script>

<style scoped>
.bg-stripes {
    background-image: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.15) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.15) 50%,
        rgba(255, 255, 255, 0.15) 75%,
        transparent 75%,
        transparent
    );
    background-size: 1rem 1rem;
}

@keyframes slide {
    0% {
        background-position: 0 0;
    }
    100% {
        background-position: 1rem 0;
    }
}

.animate-slide {
    animation: slide 1s linear infinite;
}
</style>